/* @flow */

import React from 'react';
import { connect } from 'react-redux';

import Box from '@material-ui/core/Box';

import SupplierCompany from 'models/confirming/supplierCompany';
import Document from 'models/documents/v2/document';
import DocumentType from 'models/documents/documentType';

import ConfirmingService from 'services/ConfirmingService';

import LoadingComponent from 'components/loading';

import { isCountryFranceOrDomTom } from 'lib/countryHelpers';
import NotificationHelper from 'lib/notifications';
import ConfirmingAddSupplierTab3ViewComponent from './view';
import DocumentService from 'services/DocumentService';
import { DOC_CODE_RIB, DOC_TYPE_RIB, DOC_CODE_BKA, DOC_TYPE_BKA, DOC_TYPE_SALESMANDATE, DOC_CODE_SALESMANDATE } from 'constants/constants';

type ConfirmingAddSupplierTab3ComponentProps = {
  activeCompanyId: number;
  supplierId: number;
  supplierCompany: SupplierCompany;
  successHandler: () => void;
}

type ConfirmingAddSupplierTab3ComponentState = {
  isLoading: boolean;
  isProcessing: boolean;
  document: Document;
  salesMandateDocument: Document;
  supplierCompany: SupplierCompany;
  hasBankAccountDocument: boolean;
}

class ConfirmingAddSupplierTab3Component extends React.Component<ConfirmingAddSupplierTab3ComponentProps, ConfirmingAddSupplierTab3ComponentState> {

  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
    };
  }

  componentDidMount = async () => {
    this.refreshData();
  }

  refreshData = async () => {
    try {
      let supplierCompany = await ConfirmingService.getConfirmingSupplierDetails(this.props.activeCompanyId, this.props.supplierId);

      let documentType = new DocumentType();
      if (isCountryFranceOrDomTom(supplierCompany.bankAccountCountryId)) {
        documentType = new DocumentType(DOC_TYPE_RIB, DOC_CODE_RIB);
      } else {
        documentType = new DocumentType(DOC_TYPE_BKA, DOC_CODE_BKA);
      }
      const document = new Document();
      document.typeId = documentType.typeId;
      document.code = documentType.code;
      document.isRequired = false;
      const salesMandateDocument = new Document();
      salesMandateDocument.typeId = DOC_TYPE_SALESMANDATE;
      salesMandateDocument.code = DOC_CODE_SALESMANDATE;
      salesMandateDocument.isRequired = false;

      this.setState({
        supplierCompany,
        isLoading: false,
        isProcessing: false,
        document,
        salesMandateDocument,
        hasBankAccountDocument: supplierCompany.hasBankAccountDocument
      });
    } catch (e) {
      console.error(e);
    }
  }

  handleDocumentSelect = (file: File) => {
    const document = this.state.document;
    document.file = file;
    document.filename = file.name;
    document.isInvalid = false;
    document.isMissing = false;
    this.setState({ document });
  };
  handleDocumentSelectFailure = (file: File) => {
    const document = this.state.document;
    document.file = null;
    document.filename = file.name;
    document.isInvalid = true;
    document.isMissing = false;
    this.setState({ document });
  }
  handleDocumentRemove = () => {
    const document = this.state.document;
    document.file = null;
    document.filename = '';
    document.isInvalid = false;
    document.isMissing = false;
    this.setState({ document });
  };
  handleSalesMandateDocumentSelect = (file: File) => {
    const salesMandateDocument = this.state.salesMandateDocument;
    salesMandateDocument.file = file;
    salesMandateDocument.filename = file.name;
    salesMandateDocument.isInvalid = false;
    salesMandateDocument.isMissing = false;
    this.setState({ salesMandateDocument });
  };
  handleSalesMandateDocumentSelectFailure = (file: File) => {
    const salesMandateDocument = this.state.salesMandateDocument;
    salesMandateDocument.file = null;
    salesMandateDocument.filename = file.name;
    salesMandateDocument.isInvalid = true;
    salesMandateDocument.isMissing = false;
    this.setState({ salesMandateDocument });
  }
  handleSalesMandateDocumentRemove = () => {
    const salesMandateDocument = this.state.salesMandateDocument;
    salesMandateDocument.file = null;
    salesMandateDocument.filename = '';
    salesMandateDocument.isInvalid = false;
    salesMandateDocument.isMissing = false;
    this.setState({ salesMandateDocument });
  };


  skipStep = () => {
    this.setState({ isProcessing: true });
    this.props.successHandler();
  }
  submitForm = async () => {
    try {
      const document = this.state.document;
      const salesMandateDocument = this.state.salesMandateDocument;

      if (document.file === null || document.isMissing || document.isInvalid)
        document.isMissing = true;
      if (salesMandateDocument.file === null || salesMandateDocument.isMissing || salesMandateDocument.isInvalid)
        salesMandateDocument.isMissing = true;

      if (document.isMissing && salesMandateDocument.isMissing){
        this.setState({ document, isProcessing: false });
        return;
      }

      if (document.file !== null && !document.isMissing && !document.isInvalid)
        await this.submitDocument(document);
      if (salesMandateDocument.file !== null && !salesMandateDocument.isMissing && !salesMandateDocument.isInvalid)
        await this.submitDocument(salesMandateDocument);

      this.props.successHandler();
    } catch (e) {
      console.error(e);
      NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
      this.setState({ isProcessing: false });
    }
  }

  submitDocument = async (document: Document) => {
    await DocumentService.sendSupplierDocument(this.props.activeCompanyId, this.props.supplierId, document.typeId, document.file);
  }

  render() {
    if (this.state.isLoading) {
      return (<LoadingComponent />);
    }

    return (
      <React.Fragment>
        <Box mt={4}>
          <ConfirmingAddSupplierTab3ViewComponent
            supplierCompanyDetails={this.state.supplierCompany}
            isProcessing={this.state.isProcessing}
            handleDocumentSelect={this.handleDocumentSelect}
            handleDocumentSelectFailure={this.handleDocumentSelectFailure}
            handleDocumentRemove={this.handleDocumentRemove}
            handleSalesMandateDocumentSelect={this.handleSalesMandateDocumentSelect}
            handleSalesMandateDocumentSelectFailure={this.handleSalesMandateDocumentSelectFailure}
            handleSalesMandateDocumentRemove={this.handleSalesMandateDocumentRemove}
            document={this.state.document}
            salesMandateDocument={this.state.salesMandateDocument}
            submitForm={this.submitForm}
            skipStep={this.skipStep}
          />
        </Box>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  activeCompanyId: state.auth.user.activeCompany.id
});

export default connect(mapStateToProps)(ConfirmingAddSupplierTab3Component);
